import React, {memo, useState} from 'react'
import type {ToolCallMessagePartComponent, ToolCallMessagePartProps} from '@assistant-ui/react'
import {OmicsFormattedTextBlock} from './omicsFormattedTextBlock'
import {OmicsMarkdownBody} from './omicsAssistantMarkdown'
import {formatToolArgsTextForDisplay, formatToolResultPlainText} from './formatToolInputForDisplay'
import {formatToolDisplayLabel} from './toolDisplayLabels'
import {OmicsToolCollapsibleRow} from './omicsToolCollapsibleRow'
import {formatIncompleteErrorText, formatToolResultMeta} from './workingSummary'

/** Default tool UI when no `makeAssistantToolUI` matches — compact collapsible. */
export const OmicsAssistantToolFallback: ToolCallMessagePartComponent = memo((props: ToolCallMessagePartProps) => {
  const {toolName, argsText, result, status, isError} = props
  const [open, setOpen] = useState(false)
  const label = formatToolDisplayLabel(toolName)
  const displayArgsText = formatToolArgsTextForDisplay(toolName, argsText)
  const pending = status.type === 'running'
  const failed = Boolean(isError) || status.type === 'incomplete'
  const errText = formatIncompleteErrorText(status)
  const resultText =
    !pending && result !== undefined && result !== null ? formatToolResultPlainText(result).trim() : ''

  return (
    <OmicsToolCollapsibleRow
      className='omics-aui-tool-fallback'
      title={label}
      meta={formatToolResultMeta({pending, failed, errorText: errText, resultText})}
      pending={pending}
      failed={failed}
      open={open}
      onToggle={() => setOpen((o) => !o)}
    >
      {displayArgsText ? (
        <>
          <div className='ai-chat-tool-section-label'>Parameters</div>
          <OmicsMarkdownBody markdown={`\`\`\`json\n${displayArgsText}\n\`\`\``} />
        </>
      ) : null}
      {!pending && resultText ? (
        <>
          <div className='ai-chat-tool-section-label'>Result</div>
          <OmicsFormattedTextBlock text={resultText} className='ai-chat-tool-result-output' />
        </>
      ) : null}
      {failed && errText ? (
        <div role='alert' className='ai-chat-tool-error mt-2'>
          {errText}
        </div>
      ) : null}
    </OmicsToolCollapsibleRow>
  )
})

OmicsAssistantToolFallback.displayName = 'OmicsAssistantToolFallback'
